import { motion, useReducedMotion } from "motion/react";
import { SmartImage } from "@/components/smart-image";
import { ArrowUpRight } from "@/components/icons";
import syncmeFounder from "@/assets/syncme-founder.png.asset.json";
import syncmeLogo from "@/assets/syncme-logo.png.asset.json";
import profilePhoto from "@/assets/profile-photo.webp.asset.json";
import crown from "@/assets/super-champion.png.asset.json";
import stickerUnstatus from "@/assets/stickers/lovable-status.svg.asset.json";
import stickerDiscord from "@/assets/stickers/discord.svg.asset.json";
import stickerMooody from "@/assets/stickers/mooody.svg.asset.json";

type PublicProject = {
  name: string;
  tagline: string;
  host: string;
  href?: string;
  sticker: { kind: "img" | "bolt"; src?: string; rotate: number; bg: string };
};

const PROJECTS: PublicProject[] = [
  {
    name: "Lovable Discord bot",
    tagline: "Runs the official server. 170,000 members.",
    host: "discord.gg/lovable",
    sticker: { kind: "img", src: stickerDiscord.url, rotate: -6, bg: "#ffffff" },
  },
  {
    name: "just-convert-it",
    tagline: "Free file converter. Went viral in 2025.",
    host: "just-convert-it.com",
    href: "https://just-convert-it.com",
    sticker: { kind: "bolt", rotate: 9, bg: "linear-gradient(135deg, #7ed957 0%, #c8f87a 100%)" },
  },
  {
    name: "unstatus",
    tagline: "Every Lovable status page on one screen.",
    host: "unstatus.lovable.app",
    href: "https://unstatus.lovable.app",
    sticker: { kind: "img", src: stickerUnstatus.url, rotate: -8, bg: "#f7f4ed" },
  },
  {
    name: "mooody.io",
    tagline: "Mood boards with a custom layout engine.",
    host: "mooody.io",
    href: "https://mooody.io",
    sticker: { kind: "img", src: stickerMooody.url, rotate: 7, bg: "#111111" },
  },
];

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Sticker({ s }: { s: PublicProject["sticker"] }) {
  return (
    <span
      aria-hidden
      className="grid h-12 w-12 shrink-0 place-items-center rounded-xl shadow-[0_10px_20px_-10px_rgba(20,20,60,0.35)]"
      style={{ background: s.bg, transform: `rotate(${s.rotate}deg)` }}
    >
      {s.kind === "img" ? (
        <img
          src={s.src}
          alt=""
          width={28}
          height={28}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="h-7 w-7 rounded-none object-contain"
        />
      ) : (
        <svg viewBox="0 0 24 24" className="h-7 w-7" fill="white" aria-hidden>
          <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
        </svg>
      )}
    </span>
  );
}

function ProjectCard({ p }: { p: PublicProject }) {
  const body = (
    <>
      <span className="flex items-start justify-between gap-4">
        <Sticker s={p.sticker} />
        {p.href && (
          <ArrowUpRight
            aria-hidden
            className="mt-1 h-4 w-4 text-muted-foreground transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground"
          />
        )}
      </span>
      <span className="mt-6 block font-display text-lg tracking-[-0.02em] leading-tight md:text-xl">
        {p.name}
      </span>
      <span className="mt-1.5 block text-pretty text-[15px] leading-snug text-muted-foreground">
        {p.tagline}
      </span>
      <span className="mt-5 block text-sm text-muted-foreground">{p.host}</span>
    </>
  );
  const cls =
    "group flex h-full flex-col rounded-[1.5rem] bg-background p-6 text-left ring-1 ring-border transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]";
  if (!p.href) return <div className={cls}>{body}</div>;
  return (
    <a
      href={p.href}
      target="_blank"
      rel="noreferrer"
      className={`${cls} hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(20,20,60,0.2)] active:scale-[0.99]`}
    >
      {body}
    </a>
  );
}

/**
 * Who is behind the work: Jon, one client quote, and four things we build
 * and run in public. One section, one idea: you are hiring people you can
 * check up on.
 */
export function About() {
  return (
    <section
      id="about"
      aria-labelledby="about-h"
      className="relative mx-auto max-w-[1400px] border-t border-border/60 px-6 py-20 md:px-10 md:py-32"
    >
      <h2 id="about-h" className="fluid-h2 font-display max-w-[20ch] tracking-[-0.035em]">
        Who you will be working with
      </h2>

      <div className="mt-12 grid gap-5 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="flex h-full flex-col justify-end rounded-[2rem] bg-secondary/70 p-7 ring-1 ring-foreground/[0.05] md:p-9">
            <span className="relative inline-block w-fit">
              <img
                src={profilePhoto.url}
                alt="Jon Osburg"
                width={144}
                height={144}
                loading="lazy"
                decoding="async"
                className="h-24 w-24 rounded-full object-cover ring-4 ring-background md:h-28 md:w-28"
              />
              <img
                src={crown.url}
                alt=""
                aria-hidden
                width={160}
                height={160}
                loading="lazy"
                decoding="async"
                className="pointer-events-none absolute -right-8 -top-7 w-16 rotate-[22deg] select-none rounded-none drop-shadow-[0_10px_20px_rgba(20,20,60,0.25)] md:-right-9 md:-top-8 md:w-[4.5rem]"
              />
            </span>
            <p className="mt-6 font-display text-2xl tracking-[-0.03em] leading-[1.05] md:text-3xl">
              Jon Osburg
            </p>
            <p className="mt-2 max-w-[36ch] text-pretty text-base leading-snug text-muted-foreground">
              Founder. One of two official Lovable Super Champions and community lead of the
              official Lovable Discord. On Lovable since day one.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="lg:col-span-7">
          <figure className="flex h-full flex-col justify-between rounded-[2rem] bg-background p-7 ring-1 ring-border md:p-9">
            <blockquote>
              <p
                className="font-display text-balance leading-[1.15] tracking-[-0.02em]"
                style={{ fontSize: "clamp(1.35rem, 2.2vw, 2rem)" }}
              >
                “vibing.inc migrated 8,500+ active users off Lovable Cloud onto our own
                infrastructure with almost zero downtime.”
              </p>
            </blockquote>
            <figcaption className="mt-8 flex items-center gap-3">
              <span className="relative shrink-0">
                <SmartImage
                  src={syncmeFounder.url}
                  alt=""
                  width={44}
                  height={44}
                  className="h-11 w-11 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10"
                />
                <img
                  src={syncmeLogo.url}
                  alt=""
                  width={20}
                  height={20}
                  loading="lazy"
                  decoding="async"
                  className="absolute -bottom-1 -right-1 h-5 w-5 rounded-md object-cover ring-2 ring-background"
                />
              </span>
              <span className="text-sm leading-tight">
                <span className="block text-foreground">Founder, SyncMe Pro</span>
                <span className="block text-muted-foreground">Migration off Lovable Cloud</span>
              </span>
            </figcaption>
          </figure>
        </Reveal>
      </div>

      <Reveal delay={0.12} className="mt-16 md:mt-20">
        <h3 className="font-display text-xl tracking-[-0.02em] md:text-2xl">Built and run in public</h3>
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PROJECTS.map((p) => (
            <li key={p.name}>
              <ProjectCard p={p} />
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
}
